import { useState } from "react";
import { CheckIcon, SkipForwardIcon } from "lucide-react";
import type { SlotItem } from "../utils";
import { formatDueTime } from "../utils";
import { useSlotMutations } from "../hooks/useSlotMutations";

/** How long the fill animation plays before the completion fires. */
const FILL_MS = 220;

export function UpNextSection({ items }: { items: SlotItem[] }) {
  if (items.length === 0) return null;

  return (
    <section className="space-y-2">
      <h2 className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground px-1">
        Up next
      </h2>
      <div className="rounded-2xl border border-border divide-y divide-border/60 overflow-hidden">
        {items.map((item) => (
          <UpNextRow key={item.slot.id} item={item} />
        ))}
      </div>
    </section>
  );
}

function UpNextRow({ item }: { item: SlotItem }) {
  const { complete, skip, isLoading } = useSlotMutations(item.goalId, item.slot.id);
  const [filling, setFilling] = useState(false);

  // Fill the circle with a springy check first, so completion has a visible
  // payoff even before the refetch removes the row.
  const handleComplete = () => {
    if (filling) return;
    setFilling(true);
    setTimeout(() => complete.mutate(), FILL_MS);
  };

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 transition-colors ${isLoading ? "opacity-60" : ""}`}
    >
      <button
        onClick={handleComplete}
        disabled={isLoading || filling}
        aria-label={`Complete ${item.slot.name}`}
        className={`shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
          filling
            ? "border-primary bg-primary scale-110"
            : "border-border hover:border-primary hover:bg-primary/8"
        }`}
      >
        <CheckIcon
          className={`w-3 h-3 transition-colors duration-200 ${
            filling ? "text-primary-foreground" : "text-transparent"
          }`}
          strokeWidth={3}
        />
      </button>

      <span className="shrink-0 text-xs tabular-nums text-muted-foreground bg-secondary rounded-md px-1.5 py-0.5">
        {formatDueTime(item.slot.dueTimeLocal)}
      </span>

      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium leading-snug truncate block">
          {item.slot.name}
          <span className="text-xs font-normal text-muted-foreground"> · {item.goalName}</span>
        </span>
      </div>

      <button
        onClick={() => skip.mutate()}
        disabled={isLoading}
        aria-label={`Skip ${item.slot.name} today`}
        className="shrink-0 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1.5 rounded-lg hover:bg-muted disabled:opacity-40"
      >
        <SkipForwardIcon className="w-3.5 h-3.5" />
        Skip
      </button>
    </div>
  );
}
